import React, { useState, useMemo, useEffect } from "react";
import MiniDrawer from "../../tools/MiniDrawer";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import * as XLSX from "xlsx";
import axios from "axios";
import fakeData from "./fakedataStats";
import { useMsal } from "@azure/msal-react";
import { AccountInfo, InteractionRequiredAuthError } from "@azure/msal-browser";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { FaArrowTrendUp, FaFileExcel } from "react-icons/fa6";
import { FaArrowTrendDown } from "react-icons/fa6";
import { formatNumberToFr } from "../../tools/utils/numberFormatter";
import { closeSnackbar, enqueueSnackbar } from "notistack";
const buttonStyle = (isDisabled: boolean) => ({
  color: isDisabled ? "gray" : "black",
  background: isDisabled ? "#FFEA7F" : "#FFD100", // Lighter shade when disabled
  cursor: isDisabled ? "not-allowed" : "pointer",
  height: "56px",
});

const monthToDateMapping: { [key: string]: string } = {
  Mars: "20250401",
  Juillet: "20250701",
  Octobre: "20251001",
};

const monthToIndexMapping: { [key: string]: number } = {
  Mars: 3,
  Juillet: 7,
  Octobre: 10,
};

const getFormattedDate = (month: string): string => {
  return monthToDateMapping[month] || "20250401"; // Default to "Mars" if no match
};

const isMonthDisabled = (month: string): boolean => {
  const currentMonth = new Date().getMonth() + 1; // Current month (1-based)
  const selectedMonthValue = monthToIndexMapping[month]; // Get the month index from the mapping
  return currentMonth <= selectedMonthValue; // Disable if current month is not greater than selected month +1
};

const calculatePercentageCA = (articles: any[], totalCA: number) => {
  const totalArticlesCA = articles.reduce(
    (sum, article) => sum + (article.caHT || 0),
    0
  );
  return totalCA > 0 ? ((totalArticlesCA / totalCA) * 100).toFixed(2) : "0.00";
};

const StatsMarketing: React.FC = () => {
  const [widthDrawer, setWidthDrawer] = useState("");
  const [selectedSociete, setSelectedSociete] = useState("");
  const [selectedDepot, setSelectedDepot] = useState("");
  const [selectedDate, setSelectedDate] = useState("Mars"); // Default to "Mars"
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [postArticles, setPostArticles] = useState<any>(null);
  const [lastDepotData, setLastDepotData] = useState<any[] | null>(null);

  const [societyMapping, setSocietyMapping] = useState<{
    [key: string]: {
      clientName: string;
      databaseName: string;
      socName: string;
    };
  }>({});
  const { instance, accounts, inProgress } = useMsal();
  const msalInstance = instance;
  const handleSnackbar = (message: string, severity: "success" | "error") => {
    // Implement your snackbar logic here
    enqueueSnackbar(message, {
      variant: severity,
      autoHideDuration: 3500,
      action: (key) => (
        <Button
          onClick={() => {
            closeSnackbar(key);
          }}
          style={{ color: "white", width: "10px" }}
        >
          x
        </Button>
      ),
    });
  };
  const tokenRequest = {
    scopes: ["openid", "profile", "User.Read"],
    forceRefresh: true, // Force a token refresh
  };

  const getToken = async (account: AccountInfo) => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account,
      });
      console.log(response);
      return response.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // If interaction is required, fallback to an interactive method
        const response = await msalInstance.acquireTokenPopup(tokenRequest);
        console.log(response);

        return response.idToken;
      } else {
        console.log("error token ");
        throw error;
      }
    }
  };

  const fetchUserRoles = async () => {
    try {
      const token = await getToken(accounts[0]);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BACK_URL!}/v1/apitools/users/${
          accounts[0].username
        }`
      );

      const databases = response.data.out_data.PriceComparatorRights.databases;
      const mapping: {
        [key: string]: {
          clientName: string;
          databaseName: string;
          socName: string;
        };
      } = {};

      databases.forEach((d: any) => {
        if (d.clientName === "") d.clientName = d.databaseName;
        mapping[d.socName] = {
          clientName: d.clientName, // Store clientName
          databaseName: d.databaseName, // Store databaseName
          socName: d.socName, // Store socName
        };
      });

      setSocietyMapping(mapping);

      if (databases.length === 0 || response.data.out_data.userRole === "") {
        localStorage.setItem("RightPriceComparator", "false");
      }
    } catch (error) {
      console.error("Error fetching role:", error);
      setSocietyMapping({});
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            blankrows: true, // Preserve blank rows
            defval: "", // Set default value for empty cells
          });
          console.log("Données JSON :", jsonData);

          if (jsonData.length < 2) {
            handleSnackbar(
              "Le fichier Excel ne contient pas de données valides.",
              "error"
            );
            return;
          }

          const headers: any = jsonData[1];
          const dataRows = jsonData.slice(2);

          const eanIndex = headers.indexOf("EAN ");
          const codePLTFIndex = headers.indexOf("Code PLTF");

          if (eanIndex === -1 || codePLTFIndex === -1) {
            handleSnackbar(
              "Les colonnes 'EAN' et 'Code PLTF' sont introuvables dans le fichier.",
              "error"
            );
            return;
          }

          const eanData: any = [];
          dataRows.forEach((row: any) => {
            const ean = row[eanIndex]?.toString().trim() || ""; // Create an empty EAN if not provided
            const codePLTF = row[codePLTFIndex]?.toString().trim();

            if (codePLTF) {
              // Ensure codePLTF is mandatory
              eanData.push({ ean, artcod: codePLTF });
            }
            if (ean.length > 15) {
              handleSnackbar(
                "Le fichier contient des articles au mauvais format. Respectez le format 1 ligne 1 Ean et/ou code PLTF.",
                "error"
              );
              return;
            }
          });

          if (eanData.length === 0) {
            handleSnackbar(
              "Le fichier ne contient pas de données exploitables pour les articles.",
              "error"
            );
            return;
          }

          const requestBody = {
            ean: eanData,
            ean_LENGTH: eanData.length,
          };
          setPostArticles(requestBody); // Store the request body in state
          console.log("Requête construite :", requestBody);
          handleSnackbar(
            "Fichier des articles importé avec succès.",
            "success"
          );
        } catch (error) {
          console.error("Erreur lors de la lecture du fichier :", error);
          handleSnackbar(
            "Une erreur est survenue lors de l'importation du fichier.",
            "error"
          );
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleDepotFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Fetch user roles before processing the file
    await fetchUserRoles();

    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        // Ensure the correct sheet is selected
        const sheetName =
          workbook.SheetNames.find((name) =>
            name.toLowerCase().includes("par magasin")
          ) || workbook.SheetNames[0]; // Default to the first sheet if no match

        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: true, // Preserve blank rows
          defval: "", // Set default value for empty cells
        });
        console.log("Données JSON :", jsonData);

        if (jsonData.length < 2) {
          handleSnackbar(
            "Le fichier Excel ne contient pas de données valides.",
            "error"
          );
          return;
        }

        const headers: any = jsonData[0];
        const dataRows = jsonData.slice(1);

        const depotColumnIndex = headers.indexOf("ID");
        const tfiColumnIndex = headers.indexOf("Tfi");
        const packsValidesColumnIndex = headers.indexOf("Packs validés");

        if (
          depotColumnIndex === -1 ||
          tfiColumnIndex === -1 ||
          packsValidesColumnIndex === -1
        ) {
          console.error(
            "Les colonnes 'J:ID', 'S:Tfi', et 'Q:Packs validés' sont introuvables."
          );
          handleSnackbar("Erreur fichier des dépots non valide", "error");
          return;
        }

        const depotData: any[] = [];
        dataRows.forEach((row: any) => {
          const depotValue = row[depotColumnIndex]?.toString().trim();
          const tfiValue = row[tfiColumnIndex]?.toString().trim();
          const packsValidesValue = row[packsValidesColumnIndex]
            ?.toString()
            .trim();

          if (depotValue && tfiValue === "1") {
            const socity = depotValue.slice(0, 2); // Extract the first 2 characters
            const depot = depotValue.slice(2); // Extract the rest of the string
            const operationComm = packsValidesValue === "" ? "N" : "O"; // Determine operationComm value
            console.log("Depot Value:", row);
            depotData.push({
              socity,
              depot,
              operationComm,
            });
          }
        });
        setLastDepotData(depotData);

        const formattedDate = getFormattedDate(selectedDate); // Get the formatted date based on the selected month

        const requestBody = {
          DepotData1: depotData,
          DepotData1_LENGTH: depotData.length,
        };

        console.log("Requête construite :", requestBody);
        try {
          delete axios.defaults.headers.post["Content-Type"];
          const token = await getToken(accounts[0]);
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          setLoading(true);

          // Fetch depots data with the date as a query parameter
          const depotsResponse = await axios.post(
            `${process.env
              .REACT_APP_API_BACK_URL!}/v1/apitools/MarketingStatitics/depots?date=${formattedDate}`,
            requestBody
          );

          const depotsData = depotsResponse.data.out_data.DepotData || [];
          const formattedDepots = depotsData.map((depot: any) => ({
            Societe: depot.Societe,
            Depot: depot.Depot,
            DepotCodePostal: depot.DepotCodePostal,
            DepotName: depot.DepotName,
            DepotVille: depot.DepotVille,
            operationComm: depot.operationComm,
            caTotal: depot.caTotal,
            caTotalN1: depot.caTotalN1,
            nbPassages: depot.nbPassages,
            nbPassagesN1: depot.nbPassagesN1,
            articles: [], // Articles will be fetched separately
          }));

          setData(formattedDepots);
          setLoading(false);
          handleSnackbar(
            "Données des dépôts récupérées avec succès.",
            "success"
          );
        } catch (error: any) {
          console.error("Erreur lors de l'appel API pour les dépôts :", error);
          setLoading(false);
          handleSnackbar("Erreur lors de la récupération des dépôts.", "error");
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const fetchArticlesForDepot = async (depot: string) => {
    if (!postArticles) {
      handleSnackbar(
        "Veuillez importer les articles avant d'afficher les résultats.",
        "error"
      );
      return;
    }

    console.log("fetchArticlesForDepot called with depot:", depot); // Debugging log
    const formattedDate = getFormattedDate(selectedDate);
    const databaseName = selectedSociete;

    if (!databaseName || !societyMapping[databaseName]) {
      console.error("Database introuvable pour la société sélectionnée.");
      handleSnackbar(
        "Database introuvable pour la société sélectionnée.",
        "error"
      );
      return;
    }

    const socName = Object.keys(societyMapping).find(
      (key) => societyMapping[key].socName === databaseName
    );
    let dbname = null;

    Object.keys(societyMapping).forEach((key) => {
      const value = societyMapping[key];
      if (value.socName === databaseName) {
        dbname = value.databaseName;
      }
    });
    if (!socName) {
      console.error(
        "SocName introuvable pour la société sélectionnée.",
        societyMapping,
        socName,
        databaseName
      );
      handleSnackbar(
        "SocName introuvable pour la société sélectionnée.",
        "error"
      );
      return;
    }

    try {
      console.log(
        "Fetching articles with socName:",
        socName,
        "and databaseName:",
        databaseName
      ); // Debugging log
      delete axios.defaults.headers.post["Content-Type"];
      const token = await getToken(accounts[0]);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setLoading(true);

      const requestBody = {
        ...postArticles,
      };

      const articlesResponse = await axios.post(
        `${process.env
          .REACT_APP_API_BACK_URL!}/v1/apitools/MarketingStatitics/articlesDatas?socity=${socName}&depot=${depot}&date=${formattedDate}&database=${dbname}`,
        requestBody
      );

      console.log("Articles response:", articlesResponse.data); // Debugging log

      let articlesData = articlesResponse.data.out_data.ArticleDataMrk || [];

      // Filter out articles where both nbCmdFacture and nbCmdFactureN1 are 0
      articlesData = articlesData.filter(
        (article: any) =>
          !(article.nbCmdFacture === 0 && article.nbCmdFactureN1 === 0)
      );

      // Calculate prixMoyen and prixMoyenN1
      articlesData = articlesData.map((article: any) => ({
        ...article,
        prixMoyen:
          article.volumeVente > 0 ? article.caHT / article.volumeVente : 0,
        prixMoyenN1:
          article.volumeVenteN1 > 0
            ? article.caHTN1 / article.volumeVenteN1
            : 0,
      }));

      const updatedData = data.map((item) =>
        item.Depot === depot ? { ...item, articles: articlesData } : item
      );

      setData(updatedData);
      setLoading(false);
      handleSnackbar(
        `Données des articles pour le dépôt ${depot} récupérées avec succès.`,
        "success"
      );
    } catch (error: any) {
      console.error("Erreur lors de l'appel API pour les articles :", error);
      setLoading(false);
      handleSnackbar(
        `Erreur lors de la récupération des articles pour le dépôt ${depot}.`,
        "error"
      );
    }
  };

  const fetchAllArticlesForAllDepots = async () => {
    if (!postArticles) {
      handleSnackbar(
        "Veuillez importer les articles avant d'afficher les résultats.",
        "error"
      );
      return;
    }

    const formattedDate = getFormattedDate(selectedDate);
    const databaseName = selectedSociete;

    if (!databaseName || !societyMapping[databaseName]) {
      console.error("Database introuvable pour la société sélectionnée.");
      handleSnackbar(
        "Database introuvable pour la société sélectionnée.",
        "error"
      );
      return;
    }

    const socName = Object.keys(societyMapping).find(
      (key) => societyMapping[key].socName === databaseName
    );
    let dbname: any = null;

    Object.keys(societyMapping).forEach((key) => {
      const value = societyMapping[key];
      if (value.socName === databaseName) {
        dbname = value.databaseName;
      }
    });

    if (!socName) {
      console.error(
        "SocName introuvable pour la société sélectionnée.",
        societyMapping,
        socName,
        databaseName
      );
      handleSnackbar(
        "SocName introuvable pour la société sélectionnée.",
        "error"
      );
      return;
    }

    try {
      console.log(
        "Fetching articles for all depots with socName:",
        socName,
        "and databaseName:",
        databaseName
      );

      delete axios.defaults.headers.post["Content-Type"];
      const token = await getToken(accounts[0]);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setLoading(true);

      const requests = depots.map((depot) => {
        const requestBody = {
          ...postArticles,
        };

        return axios.post(
          `${process.env
            .REACT_APP_API_BACK_URL!}/v1/apitools/MarketingStatitics/articlesDatas?socity=${socName}&depot=${depot}&date=${formattedDate}&database=${dbname}`,
          requestBody
        );
      });

      const responses = await Promise.allSettled(requests);

      const allArticles: any[] = [];
      responses.forEach((response, index) => {
        if (response.status === "fulfilled") {
          const articlesData =
            response.value.data.out_data.ArticleDataMrk || [];

          // Add DepotName, calculate prixMoyen/prixMoyenN1, and filter out articles where both nbCmdFacture and nbCmdFactureN1 are 0
          const articlesWithDepotName = articlesData
            .filter(
              (article: any) =>
                !(article.nbCmdFacture === 0 && article.nbCmdFactureN1 === 0)
            )
            .map((article: any) => {
              const matchingDepot = data.find(
                (depotData) =>
                  depotData.Depot === depots[index] &&
                  depotData.Societe === socName
              );

              return {
                ...article,
                DepotName: depots[index],
                Societe: matchingDepot ? socName : null, // Ensure the correct Societe is assigned
                DepotVille: matchingDepot ? matchingDepot.DepotVille : null, // Add Depot Ville
                DepotCodePostal: matchingDepot
                  ? matchingDepot.DepotCodePostal
                  : null, // Add Depot Code Postal
                prixMoyen:
                  article.volumeVente > 0
                    ? article.caHT / article.volumeVente
                    : 0,
                prixMoyenN1:
                  article.volumeVenteN1 > 0
                    ? article.caHTN1 / article.volumeVenteN1
                    : 0,
              };
            });

          allArticles.push(...articlesWithDepotName);
        } else {
          console.error(
            `Erreur lors de la récupération des articles pour le dépôt ${depots[index]}:`,
            response.reason
          );
        }
      });

      const updatedData = data.map((depotData) => ({
        ...depotData,
        articles: allArticles.filter(
          (article) => article.DepotName === depotData.Depot
        ),
      }));

      setData(updatedData);

      setLoading(false);
      handleSnackbar(
        "Données des articles pour tous les dépôts récupérées avec succès.",
        "success"
      );
    } catch (error: any) {
      console.error("Erreur lors de l'appel API pour les articles :", error);
      setLoading(false);
      handleSnackbar(
        "Erreur lors de la récupération des articles pour tous les dépôts.",
        "error"
      );
    }
  };

  const fetchAllArticlesForAllSocietes = async () => {
    if (!postArticles) {
      handleSnackbar(
        "Veuillez importer les articles avant d'afficher les résultats.",
        "error"
      );
      return;
    }
    try {
      setLoading(true);

      const formattedDate = getFormattedDate(selectedDate);
      const allArticlesData: any[] = [];
      const uniqueDepotsMap = new Map<
        string,
        { Societe: string; Depot: string }
      >();

      data.forEach((depot: any) => {
        const key = `${depot.Societe}-${depot.Depot}`;
        if (!uniqueDepotsMap.has(key)) {
          uniqueDepotsMap.set(key, {
            Societe: depot.Societe,
            Depot: depot.Depot,
          });
        }
      });

      const uniqueDepots = Array.from(uniqueDepotsMap.values());
      console.log("Unique depots:", uniqueDepots); // Debugging log
      for (const depot of uniqueDepots) {
        const dbname = societyMapping[depot.Societe]?.databaseName;

        if (!dbname) {
          console.error(
            `Database introuvable pour la société ${depot.Societe}`
          );
          continue;
        }

        const requestBody = {
          ...postArticles,
        };

        const articlesResponse = await axios.post(
          `${process.env
            .REACT_APP_API_BACK_URL!}/v1/apitools/MarketingStatitics/articlesDatas?socity=${
            depot.Societe
          }&depot=${depot.Depot}&date=${formattedDate}&database=${dbname}`,
          requestBody
        );

        let articlesData = articlesResponse.data.out_data.ArticleDataMrk || [];
        // Filter out invalid or empty articles
        articlesData = articlesData.filter(
          (article: any) =>
            !(article.nbCmdFacture === 0 && article.nbCmdFactureN1 === 0)
        );
        const formattedArticles = articlesData.map((article: any) => {
          const matchingDepot = data.find(
            (depotData) =>
              depotData.Depot === depot.Depot &&
              depotData.Societe === depot.Societe
          );

          return {
            ...article,
            DepotName: depot.Depot,
            Societe: depot.Societe,
            DepotVille: matchingDepot ? matchingDepot.DepotVille : null, // Add Depot Ville
            DepotCodePostal: matchingDepot
              ? matchingDepot.DepotCodePostal
              : null, // Add Depot Code Postal
            prixMoyen:
              article.volumeVente > 0 ? article.caHT / article.volumeVente : 0,
            prixMoyenN1:
              article.volumeVenteN1 > 0
                ? article.caHTN1 / article.volumeVenteN1
                : 0,
          };
        });

        allArticlesData.push(...formattedArticles);
      }

      setData((prevData) =>
        prevData.map((depot) => {
          const matchingArticles = allArticlesData.filter(
            (article) =>
              article.DepotName === depot.Depot &&
              article.Societe === depot.Societe
          );
          return {
            ...depot,
            articles:
              matchingArticles.length > 0 ? matchingArticles : depot.articles,
          };
        })
      );

      setLoading(false);
      handleSnackbar(
        "Données des articles pour toutes les sociétés récupérées avec succès.",
        "success"
      );
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des articles pour toutes les sociétés :",
        error
      );
      setLoading(false);
      handleSnackbar(
        "Erreur lors de la récupération des articles pour toutes les sociétés.",
        "error"
      );
    }
  };

  //RTaer
  const handleSocieteChange = async (value: string) => {
    if (value === "all") {
      setSelectedSociete("all"); // Set to "all" for all societes
      setSelectedDepot("all"); // Automatically select "all" depots
      await fetchAllArticlesForAllSocietes(); // Fetch all articles for all societes
    } else {
      setSelectedSociete(value); // Set the selected societe
      setSelectedDepot(""); // Reset depot selection
    }
  };

  const handleDepotChange = async (value: string) => {
    if (value === "all") {
      setSelectedDepot("all"); // Set to "all" for all depots
      await fetchAllArticlesForAllDepots(); // Fetch all articles for all depots
    } else {
      setSelectedDepot(value); // Set the selected depot
    }
  };
  const handleBarClick = (data: any) => {
    if (!postArticles) {
      handleSnackbar(
        "Veuillez importer les articles avant de cliquer sur un dépôt.",
        "error"
      );
      throw new Error("Articles not imported");
    }

    const { clientName, name } = data; // Extract society and depot from the clicked bar
    const selectedSociety = Object.keys(societyMapping).find(
      (key) => societyMapping[key].clientName === clientName
    );

    if (selectedSociety) {
      setSelectedSociete(selectedSociety); // Set the selected society
      setSelectedDepot(name); // Set the selected depot
    }
  };
  const fetchDepots = async (depotData: any[], month: string) => {
    const formattedDate = getFormattedDate(month);

    const requestBody = {
      DepotData1: depotData,
      DepotData1_LENGTH: depotData.length,
    };

    try {
      delete axios.defaults.headers.post["Content-Type"];
      const token = await getToken(accounts[0]);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setLoading(true);

      const depotsResponse = await axios.post(
        `${process.env
          .REACT_APP_API_BACK_URL!}/v1/apitools/MarketingStatitics/depots?date=${formattedDate}`,
        requestBody
      );

      const depotsData = depotsResponse.data.out_data.DepotData || [];
      const formattedDepots = depotsData.map((depot: any) => ({
        Societe: depot.Societe,
        Depot: depot.Depot,
        DepotCodePostal: depot.DepotCodePostal,
        DepotName: depot.DepotName,
        DepotVille: depot.DepotVille,
        operationComm: depot.operationComm,
        caTotal: depot.caTotal,
        caTotalN1: depot.caTotalN1,
        nbPassages: depot.nbPassages,
        nbPassagesN1: depot.nbPassagesN1,
        articles: [],
      }));

      setData(formattedDepots);
      setLoading(false);
      handleSnackbar("Données des dépôts récupérées avec succès.", "success");
    } catch (error: any) {
      console.error("Erreur lors de l'appel API pour les dépôts :", error);
      setLoading(false);
      handleSnackbar("Erreur lors de la récupération des dépôts.", "error");
    }
  };

  const filteredData = useMemo(() => {
    if (selectedSociete === "all") {
      // Combine articles from all societes when "all" is selected
      return {
        articles: data.flatMap((item: any) => item.articles || []),
      };
    }
    if (selectedDepot === "all") {
      // Combine articles from all depots when "all" is selected
      return {
        articles: data
          .filter((item: any) => item.Societe === selectedSociete)
          .flatMap((item: any) => item.articles || []),
      };
    }
    if (selectedDepot) {
      // Find the specific depot if one is selected
      return data.find(
        (item: any) =>
          item.Societe === selectedSociete && item.Depot === selectedDepot
      );
    }
    return null; // Return null if no depot is selected
  }, [selectedSociete, selectedDepot, data]);

  useEffect(() => {
    if (
      filteredData &&
      filteredData.articles &&
      filteredData.articles.length > 0
    ) {
      console.log("Articles chargés :", filteredData.articles);
    } else {
      console.log("Aucun article trouvé pour les critères sélectionnés.");
    }
  }, [filteredData]);

  useEffect(() => {
    if (selectedDepot && selectedDepot !== "all") {
      console.log("selectedDepot changed:", selectedDepot); // Debugging log
      fetchArticlesForDepot(selectedDepot);
    }
  }, [selectedDepot]);

  const handleExportToExcel = () => {
    const depotData = data.map((depot) => ({
      Société: depot.Societe,
      Dépôt: depot.Depot,
      Ville: depot.DepotVille,
      CodePostal: depot.DepotCodePostal,
      "Opération Commerciale": depot.operationComm === "O" ? "Oui" : "Non",
      "CA Total N": depot.caTotal,
      "CA Total N-1": depot.caTotalN1,
      "Nb Passages N": depot.nbPassages,
      "Nb Passages N-1": depot.nbPassagesN1,
    }));

    const articleData = data.flatMap((depot) =>
      depot.articles.map((article: any) => ({
        Société: depot.Societe,
        Dépôt: depot.Depot,
        EAN: article.ean,
        Libellé: article.lib,
        Famille: article.fam,
        Groupe: article.grp,
        "Nb Cmd Facturé N": article.nbCmdFacture,
        "Nb Cmd Facturé N-1": article.nbCmdFactureN1,
        "Volume Vente N": article.volumeVente,
        "Volume Vente N-1": article.volumeVenteN1,
        "CA HT N": article.caHT.toFixed(2),
        "CA HT N-1": article.caHTN1.toFixed(2),
        "Prix Moyen N": article.prixMoyen.toFixed(2),
        "Prix Moyen N-1": article.prixMoyenN1.toFixed(2),
        "Unité de Vente": article.UV,
      }))
    );

    const workbook = XLSX.utils.book_new();
    const depotSheet = XLSX.utils.json_to_sheet(depotData);
    const articleSheet = XLSX.utils.json_to_sheet(articleData);

    XLSX.utils.book_append_sheet(workbook, depotSheet, "Dépôts");
    XLSX.utils.book_append_sheet(workbook, articleSheet, "Articles");

    XLSX.writeFile(workbook, "StatsMarketing.xlsx");
  };

  const societes = useMemo(() => {
    const societyDepotMap: { [key: string]: Set<string> } = {};

    data.forEach((item: any) => {
      if (!societyDepotMap[item.Societe]) {
        societyDepotMap[item.Societe] = new Set();
      }
      societyDepotMap[item.Societe].add(item.Depot);
    });

    return Object.keys(societyDepotMap).map((databaseName) => ({
      socName: databaseName,
      clientName: societyMapping[databaseName]?.clientName || databaseName, // Map databaseName to clientName
      depots: Array.from(societyDepotMap[databaseName]), // List of depots for the society
    }));
  }, [data, societyMapping]);

  const depots = useMemo(() => {
    return data
      .filter((item: any) => item.Societe === selectedSociete)
      .map((item: any) => item.Depot);
  }, [selectedSociete, data]);

  const depotComparisonChartData = useMemo(() => {
    return data.map((depot: any) => ({
      name: depot.Depot,
      depotVille: depot.DepotVille,
      depotCodePostal: depot.DepotCodePostal,
      caTotal: depot.caTotal,
      caTotalN1: depot.caTotalN1,
      operationComm: depot.operationComm,
      clientName: societyMapping[depot.Societe]?.clientName || depot.Societe, // Use clientName for display
    }));
  }, [data, societyMapping]);

  const commercialOperationStats = useMemo(() => {
    const totalDepots = data.length;
    const activeDepots = data.filter(
      (item: any) => item.operationComm === "O"
    ).length;
    return {
      totalDepots,
      activeDepots,
      inactiveDepots: totalDepots - activeDepots,
    };
  }, [data]);

  const totalSocieteStats = useMemo(() => {
    if (!selectedSociete) return null;

    const depotsForSociete = data.filter(
      (item: any) => item.Societe === selectedSociete
    );

    const totalCA = depotsForSociete.reduce(
      (sum: number, depot: any) => sum + (depot.caTotal || 0),
      0
    );
    const totalCAN1 = depotsForSociete.reduce(
      (sum: number, depot: any) => sum + (depot.caTotalN1 || 0),
      0
    );
    const totalPassages = depotsForSociete.reduce(
      (sum: number, depot: any) => sum + (depot.nbPassages || 0),
      0
    );
    const totalPassagesN1 = depotsForSociete.reduce(
      (sum: number, depot: any) => sum + (depot.nbPassagesN1 || 0),
      0
    );

    return { totalCA, totalCAN1, totalPassages, totalPassagesN1 };
  }, [selectedSociete, data]);

  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  return (
    <MiniDrawer width={widthDrawer} setWidth={setWidthDrawer}>
      <Box
        style={{ flexGrow: 1, padding: "85px 0 0 32px", flexDirection: "row" }}
      >
        <div style={{ flexGrow: 1, alignItems: "center", width: widthDrawer }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              Accueil
            </Link>
            <Typography color="text.primary">Statistiques Marketing</Typography>
          </Breadcrumbs>
          <Typography
            variant="h3"
            style={{
              margin: "10px 10px 10px 0",
              fontWeight: "bold",
              fontSize: 32,
            }}
          >
            Statistiques Marketing
          </Typography>

          <Grid
            container
            spacing={2}
            style={{
              marginBottom: 16,
              marginTop: 16,
              display: "flex",
              alignItems: "center", // Align items vertically
            }}
          >
            <Grid item sx={{ marginTop: "18px" }}>
              <Button
                variant="contained"
                component="label"
                style={buttonStyle(loading)} // Apply button style
                disabled={loading} // Disable button when loading
                sx={{ marginTop: "-22px" }}
              >
                {loading ? (
                  <CircularProgress size={24} style={{ color: "#000" }} />
                ) : (
                  <>
                    <CloudUploadIcon style={{ marginRight: 8 }} />
                    Importer les dépôts
                  </>
                )}
                <input
                  type="file"
                  hidden
                  accept=".xlsx, .xls"
                  onChange={handleDepotFileUpload}
                />
              </Button>
            </Grid>

            {data.length > 0 && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth style={{ maxWidth: 300 }}>
                  <InputLabel id="date-select-label" sx={{ color: "black" }}>
                    Date
                  </InputLabel>
                  <Select
                    labelId="date-select-label"
                    value={selectedDate}
                    onChange={(e) => {
                      const newMonth = e.target.value;
                      setSelectedDate(newMonth);

                      if (lastDepotData) {
                        fetchDepots(lastDepotData, newMonth);
                      }
                      // Trigger request after selecting the month
                      const formattedDate = getFormattedDate(e.target.value);
                      console.log(
                        "Selected month:",
                        e.target.value,
                        "Formatted date:",
                        formattedDate
                      );
                    }}
                    sx={{
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black", // Set border color to black when focused
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black", // Set border color to black on hover
                      },
                    }}
                    label="Date"
                  >
                    {Object.keys(monthToIndexMapping).map((month) => (
                      <MenuItem
                        key={month}
                        value={month}
                        disabled={isMonthDisabled(month)} // Disable based on the condition
                        style={{
                          color: isMonthDisabled(month) ? "#BDBDBD" : "inherit", // Gray out if disabled
                        }}
                      >
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {data.length > 0 && postArticles && (
              <Grid item style={{ marginLeft: "auto" }}>
                <Button
                  variant="contained"
                  style={buttonStyle(false)} // Apply button style
                  onClick={handleExportToExcel}
                >
                  <FaFileExcel style={{ marginRight: 8 }} />
                  Exporter en Excel
                </Button>
              </Grid>
            )}
          </Grid>

          {data.length > 0 && (
            <>
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        Dépôts avec opérations activées / Total dépôts
                      </Typography>
                      <Typography variant="h4" style={{ color: "#FFD100" }}>
                        {commercialOperationStats.activeDepots} /{" "}
                        {commercialOperationStats.totalDepots}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Paper
                elevation={3}
                style={{ padding: "16px", marginBottom: 16 }}
              >
                <Typography
                  variant="h5"
                  style={{ margin: "20px 0", fontWeight: "bold" }}
                >
                  Comparaison des CA et passages par dépôt ({selectedDate})
                </Typography>
                <Card
                  style={{
                    padding: "16px",
                    overflowX: "auto",
                  }}
                >
                  <ResponsiveContainer
                    width={Math.max(depotComparisonChartData.length * 100, 800)}
                    height={500}
                  >
                    <BarChart
                      data={depotComparisonChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      onClick={(event) => {
                        if (
                          event &&
                          event.activePayload &&
                          event.activePayload.length > 0
                        ) {
                          handleBarClick(event.activePayload[0].payload); // Handle bar click
                        }
                      }}
                    >
                      <Legend
                        wrapperStyle={{ marginTop: -25, textAlign: "left" }}
                        align="left"
                        verticalAlign="top"
                        payload={[
                          {
                            id: "operationComm",
                            type: "square",
                            value: "Opération commerciale activée",
                            color: "#4caf50", // Green for active operations
                          },
                          {
                            id: "operationCommInactive",
                            type: "square",
                            value: "Opération commerciale non activée",
                            color: "#757575", // Dark grey for inactive operations
                          },
                        ]}
                      />
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `${value} €`} />
                      <Tooltip
                        formatter={(value: any, name: any, props: any) => {
                          const { payload } = props;
                          const depotVille = payload?.depotVille || "N/A";
                          const depotCodePostal =
                            payload?.depotCodePostal || "N/A";
                          return [
                            `${value} €`,
                            `${name} (Ville: ${depotVille}, Code Postal: ${depotCodePostal})`,
                          ];
                        }}
                        labelFormatter={(label: any) => `Dépôt: ${label}`}
                        contentStyle={{
                          backgroundColor: "white",
                          color: "black",
                        }}
                      />
                      <Bar
                        dataKey="caTotal"
                        name="CA Total"
                        barSize={30}
                        radius={[10, 10, 0, 0]}
                      >
                        {depotComparisonChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry.operationComm === "O"
                                ? "#4caf50"
                                : "#757575"
                            } // Green for active, dark grey for inactive
                          />
                        ))}
                      </Bar>
                      <Bar
                        dataKey="caTotalN1"
                        name="CA Total N-1"
                        fill="#9e9e9e" // Light grey for N-1
                        barSize={30}
                        radius={[10, 10, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Paper>

              <Grid
                container
                spacing={2}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Grid item>
                  <Button
                    variant="contained"
                    component="label"
                    style={buttonStyle(loading)} // Apply button style
                    disabled={loading} // Disable button when loading
                    sx={{ marginTop: "13px" }}
                  >
                    {loading ? (
                      <CircularProgress size={24} style={{ color: "#000" }} />
                    ) : (
                      <>
                        <CloudUploadIcon style={{ marginRight: 8 }} />
                        Importer les articles
                      </>
                    )}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx, .xls"
                      onChange={handleFileUpload}
                    />
                  </Button>
                </Grid>
                <Grid item style={{ display: "inline-flex" }}>
                  <FormControl
                    fullWidth
                    style={{
                      maxWidth: 300,
                      marginTop: 16,
                      width: 300,
                      backgroundColor: postArticles ? "#e8f5e9" : "#fbe9e7", // Light green if imported, light red otherwise
                      color: "black", // Set input text color to black
                    }}
                    disabled={!postArticles || loading} // Disable if articles are not imported or loading
                  >
                    <InputLabel
                      id="societe-select-label"
                      style={{ color: "black" }} // Set label text color to black
                    >
                      Société
                    </InputLabel>
                    <Select
                      labelId="societe-select-label"
                      value={selectedSociete}
                      onChange={(e) => handleSocieteChange(e.target.value)}
                      label="Société"
                      style={{ color: "black" }} // Set dropdown text color to black
                      MenuProps={{
                        PaperProps: {
                          style: {
                            backgroundColor: "white", // Ensure dropdown background is white
                          },
                        },
                      }}
                      sx={{
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "black", // Set border color to black when focused
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "black", // Set border color to black on hover
                        },
                      }}
                    >
                      <MenuItem value="all" style={{ color: "black" }}>
                        Toutes les sociétés
                      </MenuItem>
                      {societes
                        .sort((a, b) =>
                          a.clientName.localeCompare(b.clientName)
                        )
                        .map((societe) => (
                          <MenuItem
                            key={societe.socName}
                            value={societe.socName}
                            style={{ color: "black" }} // Set menu item text color to black
                          >
                            {societe.clientName
                              .toLowerCase()
                              .replace(/^\w/, (c) => c.toUpperCase())}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedSociete && (
                  <Grid item style={{ display: "inline-flex" }}>
                    <FormControl
                      fullWidth
                      style={{
                        maxWidth: 300,
                        marginTop: 16,
                        width: 300,
                        backgroundColor: postArticles ? "#e8f5e9" : "#fbe9e7", // Light green if imported, light red otherwise
                        color: "black", // Set input text color to black
                      }}
                      disabled={!postArticles || loading} // Disable if articles are not imported or loading
                    >
                      <Select
                        labelId="depot-select-label"
                        value={selectedDepot} // Use the current value of `selectedDepot`
                        onChange={(e) => handleDepotChange(e.target.value)}
                        displayEmpty
                        style={{ color: "black" }} // Set dropdown text color to black
                        MenuProps={{
                          PaperProps: {
                            style: {
                              backgroundColor: "white", // Ensure dropdown background is white
                            },
                          },
                        }}
                        sx={{
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "black", // Set border color to black when focused
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "black", // Set border color to black on hover
                          },
                        }}
                      >
                        <MenuItem value="" disabled style={{ color: "black" }}>
                          Sélectionnez un dépôt
                        </MenuItem>
                        <MenuItem value="all" style={{ color: "black" }}>
                          Tous les dépôts
                        </MenuItem>
                        {depots.map((depot) => (
                          <MenuItem
                            key={depot}
                            value={depot}
                            style={{ color: "black" }} // Set menu item text color to black
                          >
                            {depot}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>

              {loading && selectedDepot === "all" ? (
                <Typography
                  variant="h6"
                  style={{
                    margin: "20px 0",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {/* Chargement des données des articles... */}
                </Typography>
              ) : (
                selectedDepot === "all" &&
                filteredData &&
                filteredData.articles.length > 0 && (
                  <>
                    <Typography
                      variant="h5"
                      style={{ margin: "20px 0", fontWeight: "bold" }}
                    >
                      Détails des articles pour tous les dépôts ({selectedDate})
                    </Typography>
                    {selectedSociete && totalSocieteStats && (
                      <>
                        <Grid
                          container
                          spacing={2}
                          style={{ marginBottom: 16 }}
                        >
                          <Grid item>
                            <Card>
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  sx={{ fontWeight: "bold" }}
                                >
                                  CA Total de la Société ({currentYear} vs{" "}
                                  {previousYear})
                                </Typography>
                                <Typography variant="h4">
                                  {totalSocieteStats.totalCA >
                                  totalSocieteStats.totalCAN1 ? (
                                    <FaArrowTrendUp
                                      style={{
                                        color: "#4CAF50",
                                        marginLeft: 8,
                                      }} // Friendly green
                                    />
                                  ) : totalSocieteStats.totalCA <
                                    totalSocieteStats.totalCAN1 ? (
                                    <FaArrowTrendDown
                                      style={{
                                        color: "#F44336",
                                        marginLeft: 8,
                                      }} // Friendly red
                                    />
                                  ) : null}{" "}
                                  <span
                                    style={{
                                      color:
                                        totalSocieteStats.totalCA >
                                        totalSocieteStats.totalCAN1
                                          ? "#4CAF50" // Friendly green
                                          : "#F44336", // Friendly red
                                    }}
                                  >
                                    {formatNumberToFr(
                                      totalSocieteStats.totalCA
                                    )}{" "}
                                    €
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        totalSocieteStats.totalCAN1 >
                                        totalSocieteStats.totalCA
                                          ? "#4CAF50" // Friendly green
                                          : "#F44336", // Friendly red
                                    }}
                                  >
                                    {formatNumberToFr(
                                      totalSocieteStats.totalCAN1
                                    )}{" "}
                                    €
                                  </span>
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item>
                            <Card>
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Nombre de Passages de la Société (
                                  {currentYear} vs {previousYear})
                                </Typography>
                                <Typography variant="h4">
                                  <span
                                    style={{
                                      color:
                                        totalSocieteStats.totalPassages >
                                        totalSocieteStats.totalPassagesN1
                                          ? "#4CAF50" // Friendly green
                                          : "#F44336", // Friendly red
                                    }}
                                  >
                                    {totalSocieteStats.totalPassages >
                                    totalSocieteStats.totalPassagesN1 ? (
                                      <FaArrowTrendUp
                                        style={{
                                          color: "#4CAF50",
                                          marginLeft: 8,
                                        }} // Friendly green
                                      />
                                    ) : totalSocieteStats.totalPassages <
                                      totalSocieteStats.totalPassagesN1 ? (
                                      <FaArrowTrendDown
                                        style={{
                                          color: "#F44336",
                                          marginLeft: 8,
                                        }} // Friendly red
                                      />
                                    ) : null}{" "}
                                    {totalSocieteStats.totalPassages}
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        totalSocieteStats.totalPassagesN1 >
                                        totalSocieteStats.totalPassages
                                          ? "#4CAF50" // Friendly green
                                          : "#F44336", // Friendly red
                                    }}
                                  >
                                    {totalSocieteStats.totalPassagesN1}
                                  </span>
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item>
                            <Card>
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Pourcentage de CA des articles par rapport au
                                  CA total de la société
                                </Typography>
                                <Typography
                                  variant="h4"
                                  style={{ color: "#FFD100" }}
                                >
                                  {calculatePercentageCA(
                                    filteredData.articles,
                                    totalSocieteStats.totalCA
                                  )}
                                  %
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </>
                    )}
                    <Paper
                      elevation={3}
                      style={{ padding: "16px", marginBottom: 16 }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Société
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Dépôt
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Ville
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Code Postal
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              EAN / Code PLTF
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Libellé
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Famille
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              Groupe
                            </TableCell>
                            <TableCell
                              align="center"
                              style={{ fontWeight: "bold" }}
                            >
                              Nombre Commande ({currentYear} vs {previousYear})
                            </TableCell>
                            <TableCell
                              align="center"
                              style={{ fontWeight: "bold" }}
                            >
                              Volume ({currentYear} vs {previousYear})
                            </TableCell>
                            <TableCell
                              align="center"
                              style={{ fontWeight: "bold" }}
                            >
                              CA HT ({currentYear} vs {previousYear})
                            </TableCell>
                            <TableCell
                              align="center"
                              style={{ fontWeight: "bold" }}
                            >
                              Prix Moyen ({currentYear} vs {previousYear})
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredData.articles.map(
                            (article: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{article.Societe}</TableCell>
                                <TableCell>{article.DepotName}</TableCell>
                                <TableCell>{article.DepotVille}</TableCell>
                                <TableCell>{article.DepotCodePostal}</TableCell>
                                <TableCell>
                                  {article.ean || article.artcod}
                                </TableCell>
                                <TableCell>{article.lib}</TableCell>
                                <TableCell>{article.fam}</TableCell>
                                <TableCell>{article.grp}</TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.nbCmdFacture >
                                        article.nbCmdFactureN1
                                          ? "#4CAF50"
                                          : article.nbCmdFacture <
                                            article.nbCmdFactureN1
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {article.nbCmdFacture}
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.nbCmdFactureN1 >
                                        article.nbCmdFacture
                                          ? "#4CAF50"
                                          : article.nbCmdFactureN1 <
                                            article.nbCmdFacture
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {article.nbCmdFactureN1}
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.volumeVente >
                                        article.volumeVenteN1
                                          ? "#4CAF50"
                                          : article.volumeVente <
                                            article.volumeVenteN1
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {article.volumeVente}
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.volumeVenteN1 >
                                        article.volumeVente
                                          ? "#4CAF50"
                                          : article.volumeVenteN1 <
                                            article.volumeVente
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {article.volumeVenteN1}
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.caHT > article.caHTN1
                                          ? "#4CAF50"
                                          : article.caHT < article.caHTN1
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {typeof article.caHT === "number"
                                      ? formatNumberToFr(
                                          article.caHT.toFixed(2)
                                        )
                                      : "N/A"}{" "}
                                    €
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.caHTN1 > article.caHT
                                          ? "#4CAF50"
                                          : article.caHTN1 < article.caHT
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {typeof article.caHTN1 === "number"
                                      ? formatNumberToFr(
                                          article.caHTN1.toFixed(2)
                                        )
                                      : "N/A"}{" "}
                                    €
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.prixMoyen > article.prixMoyenN1
                                          ? "#4CAF50"
                                          : article.prixMoyen <
                                            article.prixMoyenN1
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {typeof article.prixMoyen === "number"
                                      ? article.prixMoyen.toFixed(2)
                                      : "N/A"}{" "}
                                    €
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.prixMoyenN1 > article.prixMoyen
                                          ? "#4CAF50"
                                          : article.prixMoyenN1 <
                                            article.prixMoyen
                                          ? "#F44336"
                                          : "inherit",
                                    }}
                                  >
                                    {typeof article.prixMoyenN1 === "number"
                                      ? article.prixMoyenN1.toFixed(2)
                                      : "N/A"}{" "}
                                    €
                                  </span>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </>
                )
              )}
              {selectedDepot && selectedDepot !== "all" && (
                <>
                  {loading ? (
                    <Typography
                      variant="h6"
                      style={{
                        margin: "20px 0",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      Chargement des données des articles...
                    </Typography>
                  ) : filteredData && filteredData.articles.length > 0 ? (
                    <>
                      <Typography
                        variant="h5"
                        style={{ margin: "20px 0", fontWeight: "bold" }}
                      >
                        Détails des articles pour le dépôt {selectedDepot} (
                        {selectedDate})
                      </Typography>

                      <Grid container spacing={2} style={{ marginBottom: 16 }}>
                        <Grid item>
                          <Card>
                            <CardContent>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: "bold" }}
                              >
                                CA Total du dépôt ({currentYear} vs{" "}
                                {previousYear})
                              </Typography>
                              <Typography variant="h4">
                                <span
                                  style={{
                                    color:
                                      filteredData.caTotal >
                                      filteredData.caTotalN1
                                        ? "#4CAF50" // Friendly green
                                        : "#F44336", // Friendly red
                                  }}
                                >
                                  {filteredData.caTotal >
                                  filteredData.caTotalN1 ? (
                                    <FaArrowTrendUp
                                      style={{
                                        color: "#4CAF50",
                                        marginLeft: 8,
                                      }} // Friendly green
                                    />
                                  ) : filteredData.caTotal <
                                    filteredData.caTotalN1 ? (
                                    <FaArrowTrendDown
                                      style={{
                                        color: "#F44336",
                                        marginLeft: 8,
                                      }} // Friendly red
                                    />
                                  ) : null}{" "}
                                  {formatNumberToFr(filteredData.caTotal)} €
                                </span>{" "}
                                vs{" "}
                                <span
                                  style={{
                                    color:
                                      filteredData.caTotalN1 >
                                      filteredData.caTotal
                                        ? "#4CAF50" // Friendly green
                                        : "#F44336", // Friendly red
                                  }}
                                >
                                  {formatNumberToFr(filteredData.caTotalN1)} €
                                </span>
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item>
                          <Card>
                            <CardContent>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: "bold" }}
                              >
                                Nombre de passages en caisse ({currentYear} vs{" "}
                                {previousYear})
                              </Typography>
                              <Typography variant="h4">
                                <span
                                  style={{
                                    color:
                                      filteredData.nbPassages >
                                      filteredData.nbPassagesN1
                                        ? "#4CAF50" // Friendly green
                                        : "#F44336", // Friendly red
                                  }}
                                >
                                  {filteredData.nbPassages >
                                  filteredData.nbPassagesN1 ? (
                                    <FaArrowTrendUp
                                      style={{
                                        color: "#4CAF50",
                                        marginLeft: 8,
                                      }} // Friendly green
                                    />
                                  ) : filteredData.nbPassages <
                                    filteredData.nbPassagesN1 ? (
                                    <FaArrowTrendDown
                                      style={{
                                        color: "#F44336",
                                        marginLeft: 8,
                                      }} // Friendly red
                                    />
                                  ) : null}{" "}
                                  {filteredData.nbPassages}
                                </span>{" "}
                                vs{" "}
                                <span
                                  style={{
                                    color:
                                      filteredData.nbPassagesN1 >
                                      filteredData.nbPassages
                                        ? "#4CAF50" // Friendly green
                                        : "#F44336", // Friendly rederere
                                  }}
                                >
                                  {filteredData.nbPassagesN1}
                                </span>
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item>
                          <Card>
                            <CardContent>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: "bold" }}
                              >
                                Pourcentage de CA des articles par rapport au CA
                                total du dépôt
                              </Typography>
                              <Typography
                                variant="h4"
                                style={{ color: "#FFD100" }}
                              >
                                {calculatePercentageCA(
                                  filteredData.articles,
                                  filteredData.caTotal
                                )}
                                %
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <Paper
                        elevation={3}
                        style={{ padding: "16px", marginBottom: 16 }}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ fontWeight: "bold" }}>
                                EAN / Code PLTF
                              </TableCell>

                              <TableCell style={{ fontWeight: "bold" }}>
                                Libellé
                              </TableCell>
                              <TableCell style={{ fontWeight: "bold" }}>
                                Dépôt
                              </TableCell>
                              <TableCell style={{ fontWeight: "bold" }}>
                                Ville
                              </TableCell>
                              <TableCell style={{ fontWeight: "bold" }}>
                                Famille
                              </TableCell>
                              <TableCell style={{ fontWeight: "bold" }}>
                                Groupe
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ fontWeight: "bold" }}
                              >
                                Nombre Commande ({currentYear} vs {previousYear}
                                )
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ fontWeight: "bold" }}
                              >
                                Volume ({currentYear} vs {previousYear})
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ fontWeight: "bold" }}
                              >
                                CA HT ({currentYear} vs {previousYear})
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ fontWeight: "bold" }}
                              >
                                Prix Moyen ({currentYear} vs {previousYear})
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredData.articles.map((article: any) => (
                              <TableRow key={article.ean || article.artcod}>
                                <TableCell>
                                  {article.ean || article.artcod}
                                </TableCell>
                                <TableCell>{article.lib}</TableCell>
                                <TableCell>{article.DepotVille}</TableCell>
                                <TableCell>{article.DepotCodePostal}</TableCell>
                                <TableCell>{article.fam}</TableCell>
                                <TableCell>{article.grp}</TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.nbCmdFacture >
                                        article.nbCmdFactureN1
                                          ? "#4CAF50" // Friendly green
                                          : article.nbCmdFacture <
                                            article.nbCmdFactureN1
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {article.nbCmdFacture}
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.nbCmdFactureN1 >
                                        article.nbCmdFacture
                                          ? "#4CAF50" // Friendly green
                                          : article.nbCmdFactureN1 <
                                            article.nbCmdFacture
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {article.nbCmdFactureN1}
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.volumeVente >
                                        article.volumeVenteN1
                                          ? "#4CAF50" // Friendly green
                                          : article.volumeVente <
                                            article.volumeVenteN1
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {article.volumeVente}
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.volumeVenteN1 >
                                        article.volumeVente
                                          ? "#4CAF50" // Friendly green
                                          : article.volumeVenteN1 <
                                            article.volumeVente
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {article.volumeVenteN1}
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.caHT > article.caHTN1
                                          ? "#4CAF50" // Friendly green
                                          : article.caHT < article.caHTN1
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {typeof article.caHT === "number"
                                      ? formatNumberToFr(
                                          article.caHT.toFixed(2)
                                        )
                                      : "N/A"}{" "}
                                    €
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.caHTN1 > article.caHT
                                          ? "#4CAF50" // Friendly green
                                          : article.caHTN1 < article.caHT
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {typeof article.caHTN1 === "number"
                                      ? formatNumberToFr(
                                          article.caHTN1.toFixed(2)
                                        )
                                      : "N/A"}{" "}
                                    €
                                  </span>
                                </TableCell>
                                <TableCell align="center">
                                  <span
                                    style={{
                                      color:
                                        article.prixMoyen > article.prixMoyenN1
                                          ? "#4CAF50" // Friendly green
                                          : article.prixMoyen <
                                            article.prixMoyenN1
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {typeof article.prixMoyen === "number"
                                      ? article.prixMoyen.toFixed(2)
                                      : "N/A"}{" "}
                                    €
                                  </span>{" "}
                                  vs{" "}
                                  <span
                                    style={{
                                      color:
                                        article.prixMoyenN1 > article.prixMoyen
                                          ? "#4CAF50" // Friendly green
                                          : article.prixMoyenN1 <
                                            article.prixMoyen
                                          ? "#F44336" // Friendly red
                                          : "inherit", // Default color
                                    }}
                                  >
                                    {typeof article.prixMoyenN1 === "number"
                                      ? article.prixMoyenN1.toFixed(2)
                                      : "N/A"}{" "}
                                    €
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </>
                  ) : (
                    <Typography
                      variant="h6"
                      style={{
                        margin: "20px 0",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      Aucune donnée d'article disponible pour le dépôt
                      sélectionné.
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Box>
    </MiniDrawer>
  );
};

export default StatsMarketing;
